import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useIntersectionReveal } from "@/hooks/useIntersectionReveal";
import { SectionHeader } from "@/components/ui/SectionHeader";

interface ProcessStep {
  number: string;
  title: string;
  description: string;
}

interface ProcessSectionProps {
  eyebrow?: string;
  headline?: string;
  subheadline?: string;
  steps?: ProcessStep[];
  serviceName?: string;
  variant?: "default" | "compact";
}

const ProcessSection = ({
  eyebrow: eyebrowProp,
  headline: headlineProp,
  subheadline: subheadlineProp,
  steps: stepsProp,
  serviceName,
}: ProcessSectionProps = {}) => {
  const { t } = useTranslation('home');

  const eyebrow = eyebrowProp || t('process.eyebrow');
  const headline = headlineProp || t('process.headline');
  const subheadline = subheadlineProp || t('process.subheadline');

  const defaultSteps: ProcessStep[] = [
    {
      number: "01",
      title: t('process.step1.title'),
      description: t('process.step1.description'),
    },
    {
      number: "02",
      title: t('process.step2.title'),
      description: t('process.step2.description'),
    },
    {
      number: "03",
      title: t('process.step3.title'),
      description: t('process.step3.description'),
    },
    {
      number: "04",
      title: t('process.step4.title'),
      description: t('process.step4.description'),
    },
    {
      number: "05",
      title: t('process.step5.title'),
      description: t('process.step5.description'),
    },
  ];

  const steps = stepsProp || defaultSteps;

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": serviceName
      ? `How to Get ${serviceName} in Houston`
      : "How to Get Your Roof Repaired or Replaced in Houston",
    "description": serviceName
      ? `${subheadline} Professional ${serviceName.toLowerCase()} services from Altum Contractors in Houston, TX.`
      : "A simple 5-step process for getting professional roofing services from Altum Contractors in Houston, TX. From free inspection to final walkthrough.",
    "totalTime": "P7D",
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "USD",
      "value": "0",
      "description": "Free inspection and estimate - no obligation"
    },
    "step": steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.title,
      "text": step.description,
      ...(index === 0 ? { "url": "https://www.altumcontractors.com/book-inspection" } : {})
    }))
  };

  const { ref: itemsRef, isVisible: itemsVisible } = useIntersectionReveal<HTMLDivElement>({
    threshold: 0.1,
  });

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
      </Helmet>
      <section id="process" className="py-24 md:py-32 bg-light-gray">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow={eyebrow}
            headline={headline}
            subheadline={subheadline}
            className="mb-16"
          />

          {/* Steps: left-aligned columns hanging from a hairline */}
          <div ref={itemsRef} className="grid gap-10 md:grid-cols-5 md:gap-6">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`reveal-up border-t border-border pt-6 text-left ${
                  itemsVisible ? "is-visible" : ""
                }`}
                style={{ transitionDelay: `${index * 120}ms` }}
              >
                <span className="font-heading text-xs font-semibold tracking-[0.08em] text-primary">
                  {step.number}
                </span>
                <h3 className="mt-4 font-heading text-xl uppercase text-navy">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ProcessSection;
